import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';
import SEO from '../../components/SEO';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Montserrat', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background-color: #f8f9fa;
  padding: 5rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 600px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const TrustBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(126, 203, 185, 0.15);
  color: var(--secondary-dark);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const Heading = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #2D3142;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
  
  span {
    color: var(--secondary);
    border-bottom: 4px solid var(--secondary);
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #6C757D;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--secondary);
  color: white;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--secondary-dark);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(126, 203, 185, 0.4);
  }
`;

const DiscountTag = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #FFAA8A;
  color: #2D3142;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transform: rotate(5deg);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const TrustLogos = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2rem;
  
  img {
    height: 40px;
    filter: grayscale(0.2);
    transition: filter 0.3s ease;
    
    &:hover {
      filter: grayscale(0);
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    
    img {
      height: 35px;
    }
  }
`;

const DisclaimerText = styled.p`
  font-size: 0.8rem;
  color: #6C757D;
  margin-top: 1rem;
  font-style: italic;
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
`;

const ResearchSection = styled.section`
  padding: 5rem 0;
  background-color: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  
  span {
    color: var(--secondary);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6C757D;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ResearchCard = styled.div`
  background-color: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    border-color: var(--secondary);
  }
`;

const ResearchIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: rgba(126, 203, 185, 0.15);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  i {
    font-size: 1.75rem;
    color: var(--secondary);
  }
`;

const ResearchTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const ResearchDescription = styled.p`
  color: #6C757D;
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const ResearchCitation = styled.p`
  font-size: 0.9rem;
  color: #adb5bd;
  font-style: italic;
  margin-bottom: 0;
`;

const BenefitsSection = styled.section`
  padding: 5rem 0;
  background-color: #f8f9fa;
`;

const BenefitCard = styled.div`
  display: flex;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BenefitIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background-color: rgba(126, 203, 185, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  
  i {
    font-size: 1.5rem;
    color: var(--secondary);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    margin-right: 0;
  }
`;

const BenefitContent = styled.div``;

const BenefitTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #2D3142;
`;

const BenefitDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 5rem 0;
  background-color: #2D3142;
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  span {
    color: var(--secondary);
  }
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--secondary);
  color: white;
  font-weight: 600;
  padding: 1rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--secondary-dark);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(126, 203, 185, 0.4);
  }
`;

const LandingPageEvidence = () => {
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Resource-Based Baby Sleep Plans | Sleep Solutions",
    description: "Our sleep plans incorporate guidelines from Australia's respected pediatric sleep resources to help support better sleep habits for your child.",
    keywords: [
      "baby sleep guidelines", 
      "Australian sleep resources", 
      "informed sleep solutions",
      "safe sleep guidelines",
      "Red Nose sleep information",
      "baby sleep resources",
      "sleep guidance",
      "Triple P sleep approach"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Dozzy Sleep Plans",
      "description": "Sleep solutions for babies and toddlers informed by Australian sleep guidelines",
      "offers": {
        "@type": "Offer",
        "price": "49.00",
        "priceCurrency": "AUD",
        "availability": "https://schema.org/InStock"
      }
    }
  };

  // Google Analytics tracking code setup
  useEffect(() => {
    // Track page view for this landing page
    ReactGA.send({
      hitType: "pageview",
      page: "/landing-evidence",
      title: "Evidence Landing Page"
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      ReactGA.event({
        category: 'engagement',
        action: 'cta_click',
        label: 'Evidence Landing Page CTA',
        value: 1
      });
    };

    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/landing-evidence"
        ogType="website"
      />
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <HeroContent>
                <TrustBadge>
                  <i className="bi bi-shield-check me-2"></i>
                  Resource-Based Sleep Solutions
                </TrustBadge>
                <Heading>
                  Resource-Based <span>Sleep Solutions</span> for Your Child
                </Heading>
                <SubHeading>
                  Our sleep plans incorporate guidelines from respected Australian sleep resources to help support better sleep habits.
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/signup" className="cta-button">
                    Get Your Sleep Plan
                  </CtaButton>
                  
                </ButtonWrapper>
                <TrustLogos>
                  <img src="/images/logo-rednose.png" alt="Red Nose" />
                  <img src="/images/logo-triple-p.png" alt="Triple P" />
                  <img src="/images/logo-raisingchildren.png" alt="Raising Children Network" />
                  <img src="/images/logo-tresilian.png" alt="Tresillian" />
                </TrustLogos>
                <DisclaimerText>
                  *Dozzy references publicly available guidelines from these organizations but is not affiliated with or endorsed by them.
                </DisclaimerText>
              </HeroContent>
            </div>
            <div className="col-lg-6">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/michael-mcauliffe-woePzpqbieM-unsplash.jpg" 
                  alt="Peaceful sleeping baby" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <ResearchSection>
        <div className="container">
          <SectionTitle>
            Built on <span>Sleep Resources</span>
          </SectionTitle>
          <SectionSubtitle>
            Our sleep plans are built on information from Australian sleep resources, enhanced with Artificial Intelligence (AI) technology.
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-md-4">
              <ResearchCard>
                <ResearchIcon>
                  <i className="bi bi-journal-medical"></i>
                </ResearchIcon>
                <ResearchTitle>Safe Sleep Guidelines</ResearchTitle>
                <ResearchDescription>
                  Our recommendations reference Red Nose Australia's safe sleeping guidelines to help promote healthy sleep habits.
                </ResearchDescription>
              </ResearchCard>
            </div>
            
            <div className="col-md-4">
              <ResearchCard>
                <ResearchIcon>
                  <i className="bi bi-graph-up"></i>
                </ResearchIcon>
                <ResearchTitle>Developmental Considerations</ResearchTitle>
                <ResearchDescription>
                  Our plans consider age-appropriate sleep needs based on information from the Raising Children Network and other resources.
                </ResearchDescription>
              </ResearchCard>
            </div>
            
            <div className="col-md-4">
              <ResearchCard>
                <ResearchIcon>
                  <i className="bi bi-people"></i>
                </ResearchIcon>
                <ResearchTitle>Positive Parenting Approach</ResearchTitle>
                <ResearchDescription>
                  We reference principles from the Triple P (Positive Parenting Program) approach to help support positive sleep habits.
                </ResearchDescription>
              </ResearchCard>
            </div>
          </div>
          
          <div className="row mt-4">
            <div className="col-md-4">
              <ResearchCard>
                <ResearchIcon>
                  <i className="bi bi-clock"></i>
                </ResearchIcon>
                <ResearchTitle>Sleep Pattern Considerations</ResearchTitle>
                <ResearchDescription>
                  Our sleep schedules are designed with consideration for natural sleep patterns and general sleep principles.
                </ResearchDescription>
              </ResearchCard>
            </div>
            
            <div className="col-md-4">
              <ResearchCard>
                <ResearchIcon>
                  <i className="bi bi-clipboard-check"></i>
                </ResearchIcon>
                <ResearchTitle>Resource Alignment</ResearchTitle>
                <ResearchDescription>
                  Our methods incorporate resources from Tresillian Family Care Centers and other respected sleep resources.
                </ResearchDescription>
              </ResearchCard>
            </div>
            
            <div className="col-md-4">
              <ResearchCard>
                <ResearchIcon>
                  <i className="bi bi-heart"></i>
                </ResearchIcon>
                <ResearchTitle>Attachment-Focused</ResearchTitle>
                <ResearchDescription>
                  Our approach aims to respect parent-child attachment and emotional wellbeing, drawing from responsive parenting concepts.
                </ResearchDescription>
              </ResearchCard>
            </div>
          </div>
        </div>
      </ResearchSection>

      <BenefitsSection>
        <div className="container">
          <SectionTitle>
            The <span>Resource-Based</span> Advantage
          </SectionTitle>
          <SectionSubtitle>
            When you choose a sleep plan built on trusted resources, you may help support your child's healthy sleep development.
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-lg-6">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-shield-check"></i>
                </BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>Safety-Minded Approach</BenefitTitle>
                  <BenefitDescription>
                    Our plans aim to prioritize your child's safety with recommendations that reference Australian safety guidelines for infant and child sleep.
                  </BenefitDescription>
                </BenefitContent>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-brain"></i>
                </BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>Supports Development</BenefitTitle>
                  <BenefitDescription>
                    Quality sleep is important for development. Our plans aim to support age-appropriate sleep patterns that may help with your child's development.
                  </BenefitDescription>
                </BenefitContent>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-calendar-check"></i>
                </BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>Long-Term Sleep Habits</BenefitTitle>
                  <BenefitDescription>
                    Our plans aim to help establish healthy sleep habits that may benefit your child throughout their development.
                  </BenefitDescription>
                </BenefitContent>
              </BenefitCard>
            </div>
            
            <div className="col-lg-6">
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-heart-pulse"></i>
                </BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>Emotional Considerations</BenefitTitle>
                  <BenefitDescription>
                    Our methods are designed with consideration for emotional security and attachment, using gentle approaches informed by child development resources.
                  </BenefitDescription>
                </BenefitContent>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-people"></i>
                </BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>Family-Centered Approach</BenefitTitle>
                  <BenefitDescription>
                    Sleep plans may work better when they consider the whole family. Our approach aims to account for parenting styles and family dynamics.
                  </BenefitDescription>
                </BenefitContent>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <i className="bi bi-graph-up"></i>
                </BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>Resource-Based Approach</BenefitTitle>
                  <BenefitDescription>
                    Our methods are designed using information from trusted sleep resources to help create personalized sleep plans for your child.
                  </BenefitDescription>
                </BenefitContent>
              </BenefitCard>
            </div>
          </div>
        </div>
      </BenefitsSection>

      <CTASection>
        <div className="container">
          <CTATitle style={{ color: 'white' }}>
            Help Your Child Develop <span>Better</span> Sleep Habits
          </CTATitle>
          <CTADescription>
            Join Australian families who are discovering personalized sleep solutions.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/signup" className="cta-button">
              Get Your Sleep Plan Now
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default LandingPageEvidence;
